// stores/userProfileStore.ts
import { defineStore } from 'pinia';

import UserService from '@/services/UserService';
import { EmptyUserProfile, PublicProfile } from '@/types/UserProfile';

interface UserProfileStoreState {
    profiles: { [userId: string]: PublicProfile };
}

const pendingFetches: Record<string, Promise<PublicProfile>> = {};

export const useUserProfileStore = defineStore('userProfile', {
    state: (): UserProfileStoreState => ({
        // profiles: { string: PublicProfileSchema.parse({}) },
        profiles: {},
    }),
    actions: {
        async getPublicProfile(userId: string): Promise<PublicProfile> {
            if (this.profiles[userId]) {
                return this.profiles[userId];
            }

            if (pendingFetches[userId] !== undefined) {
                // console.log('⏳ Awaiting pending profile fetch');
                return pendingFetches[userId];
            }

            console.log('🌐 Fetching profile from server', userId);
            pendingFetches[userId] = UserService.getPublicProfile(userId)
                .then((profile) => {
                    if (!profile) {
                        console.warn('⚠️ Profile not found for user:', userId);
                        return EmptyUserProfile;
                    }

                    this.profiles[userId] = profile;
                    return profile;
                })
                .finally(() => {
                    delete pendingFetches[userId]; // Clean up regardless of success/failure
                });

            return pendingFetches[userId];
        },

        async getPublicProfiles(userIds: string[]): Promise<PublicProfile[]> {
            const profilesToFetch: string[] = [];
            const profilesFetched: PublicProfile[] = [];

            // Check which profiles are already in the store
            for (const userId of userIds) {
                const cachedProfile = this.profiles[userId];
                // Validate that the cached profile is an instance of PublicProfile and has necessary properties
                if (cachedProfile && cachedProfile.id) {
                    profilesFetched.push(cachedProfile);
                } else {
                    profilesToFetch.push(userId);
                }
            }

            // Fetch profiles that are not in the store
            if (profilesToFetch.length > 0) {
                const fetchedProfiles = await UserService.getPublicProfiles(profilesToFetch);
                for (const profile of fetchedProfiles) {
                    this.profiles[profile.id] = profile;
                    profilesFetched.push(profile);
                }
            }

            return profilesFetched;
        },
    },
});
